import React from "react";
import styled from "styled-components";
import { ReactComponent as AttLogo } from "../pngs/ATT_Logo_lockup.svg";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import NDADisclosure from "../components/NDADisclosure";
import { DomainGrid, ContrastPair } from "../components/diagrams/primitives";
import {
    ThreeConsumers,
    SystemLayers,
    FigmaPipeline,
    Guardrails,
    ValidationLoop,
} from "../components/AttDiagrams";

/**
 * AT&T.
 *
 * The claim is narrow and defensible: a design system now has a third consumer, and
 * making it machine-readable is an engineering problem, not a prompting one. The
 * diagrams carry the structure; the pull quotes carry the argument.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const LogoWrap = styled.div`
    max-width: 190px;
    color: ${({ theme }) => theme.primary};
    filter: ${({ theme }) => theme.logoFilter};
    margin-bottom: ${spacing[700]};

    svg {
        width: 100%;
        height: auto;
    }

    ${media.md} {
        max-width: 140px;
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[500]};
    position: relative;
    margin-bottom: ${spacing[200]};

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;





const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const DOMAINS = [
    { name: "Frontend Engineering", desc: "React, TypeScript, Tailwind, reusable components, implementation patterns, frontend architecture.", color: colors.primary[500] },
    { name: "Design-System Engineering", desc: "Shared tokens, component contracts, variants, states, and patterns spanning design and code.", color: colors.cyan[300] },
    { name: "AI Developer Tooling", desc: "Rules, commands, context, and workflows for AI-assisted frontend development.", color: colors.amber[400] },
    { name: "Design-to-Code Infrastructure", desc: "Connecting Figma structures with production component architecture.", color: colors.emerald[500] },
    { name: "Context Engineering", desc: "Structuring repository, design-system, and implementation knowledge so agents decide better.", color: colors.purple[400] },
    { name: "Validation", desc: "Guardrails around generated code rather than trusting model output blindly.", color: colors.coral[400] },
];

const Att = () => (
    <PageWrapper>
        <Header>
            <LogoWrap>
                <AttLogo />
            </LogoWrap>
            <PageHeadline>AT&amp;T</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Senior Product Designer</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Focus</TerminalLabel>
                    <span>React &middot; TypeScript &middot; Design Systems &middot; AI Tooling</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>2025 &mdash; Present</span>
                </MetaItem>
            </Meta>
            <Lede>
                As AI coding tools became part of everyday development, a new problem showed up:{" "}
                <strong>AI could generate frontend code quickly, but it did not understand our
                product system.</strong> Left unconstrained, an agent picks the wrong component,
                invents styles, rebuilds something that already exists, or produces an interface that
                looks right while violating the design system underneath.
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="2025 – Present"
                    roles={["Senior Product Designer", "Design Systems", "AI Developer Tooling"]}
                    tools={["React", "TypeScript", "Tailwind CSS", "Figma MCP", "AI Guardrails"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        A design system is traditionally built for two audiences: designers working
                        in Figma, and engineers working in a component library. AI introduced a
                        third, and it does not absorb the same rules by osmosis.
                    </Para>
                    <DiagramSlot>
                        <ThreeConsumers />
                    </DiagramSlot>
                    <Para>An agent needs to be told, explicitly:</Para>
                    <Bullets>
                        <Bullet>Which components already exist</Bullet>
                        <Bullet>When each component should be used</Bullet>
                        <Bullet>Which variants are valid</Bullet>
                        <Bullet>Which design tokens are allowed</Bullet>
                        <Bullet>How components can be composed</Bullet>
                        <Bullet>Which implementation patterns are prohibited</Bullet>
                        <Bullet>How a Figma design maps to production components</Bullet>
                    </Bullets>
                    <Pull>
                        How do we make the design system machine-readable enough for an AI agent to
                        reliably build with it?
                    </Pull>
                </Section>

                <Section>
                    <SectionHeadline>The Design System as Infrastructure</SectionHeadline>
                    <Para>
                        Rather than treating it as a collection of visual components, I approached
                        the design system as infrastructure shared between design, engineering, and
                        AI &mdash; which meant creating much stronger relationships between design
                        decisions and implementation contracts.
                    </Para>
                    <DiagramSlot>
                        <SystemLayers />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>A Constrained Vocabulary</SectionHeadline>
                    <Para>
                        We built the component system around reusable frontend patterns, with a Figma
                        library modelled closely after the implementation. Components define their
                        supported variants, states, sizing, spacing, typography, interaction
                        behavior, composition rules, and accessibility expectations.
                    </Para>
                    <Para>
                        Instead of unlimited freedom to invent interfaces, the agent gets a
                        vocabulary. The difference sounds small and is not:
                    </Para>
                    <ContrastPair
                        left={{
                            label: "Unconstrained",
                            color: colors.coral[400],
                            text: "\u201CGenerate a button that looks like this.\u201D",
                        }}
                        right={{
                            label: "Constrained",
                            color: colors.emerald[500],
                            text: "\u201CUse our Button component with these supported properties.\u201D",
                        }}
                    />
                </Section>

                <Section>
                    <SectionHeadline>Connecting Figma to Engineering</SectionHeadline>
                    <Para>
                        Using Figma-connected tooling, the coding workflow can inspect a design and
                        understand the structure behind it &mdash; reasoning about the components and
                        patterns a design represents rather than interpreting it from pixels.
                    </Para>
                    <DiagramSlot>
                        <FigmaPipeline />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Guardrails</SectionHeadline>
                    <Para>
                        Context alone was not enough. Agents also need explicit boundaries, so I
                        helped define reusable instructions, commands, and hard rules governing how
                        AI generates frontend code &mdash; shifting the workflow from unconstrained
                        generation to <strong>constrained generation</strong>.
                    </Para>
                    <DiagramSlot>
                        <Guardrails />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Context Engineering</SectionHeadline>
                    <Para>
                        The biggest lesson was that output quality depends far more on the context
                        around the model than on the model itself. The working architecture is closer
                        to <strong>model + repository context + component documentation + design
                        context + rules + validation</strong>.
                    </Para>
                    <Para>
                        When generated code failed, the fix often was not a better prompt or a bigger
                        model. The system was missing information. That changed the question I ask:
                    </Para>
                    <ContrastPair
                        left={{
                            label: "Before",
                            color: colors.coral[400],
                            text: "\u201CHow do I get the AI to write better code?\u201D",
                        }}
                        right={{
                            label: "After",
                            color: colors.emerald[500],
                            text: "\u201CWhat information does the AI need to make the same decision an engineer would make?\u201D",
                        }}
                    />
                    <Para>
                        We encoded common workflows into reusable AI instructions and commands, so a
                        developer could invoke one with project context and constraints already
                        attached. That reduced dependence on individual prompt-writing ability and
                        made AI-assisted development repeatable across engineers.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Validation Matters as Much as Generation</SectionHeadline>
                    <Para>
                        Generating code is only half the problem. The system also needs a way to
                        decide whether the output followed the rules.
                    </Para>
                    <DiagramSlot>
                        <ValidationLoop />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Developer Experience</SectionHeadline>
                    <Para>
                        The goal was never to prove AI could generate interfaces. It was to cut the
                        repetitive translation between product, design, and engineering &mdash; the
                        loop that traditionally runs design, documentation, engineer interpretation,
                        implementation, design review, corrections.
                    </Para>
                    <Para>
                        Bringing design-system knowledge directly into the engineering environment
                        shortened that loop, so developers spend less time reconstructing established
                        patterns and more time on actual product problems.
                    </Para>
                    <DiagramSlot>
                        <DomainGrid label="What the Work Spanned" domains={DOMAINS} />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>The Bigger Lesson</SectionHeadline>
                    <Para>
                        AI generates code incredibly quickly. Speed is not the difficult problem.
                    </Para>
                    <Pull>Speed isn&rsquo;t the difficult problem. Consistency is.</Pull>
                    <Para>
                        The more development becomes AI-assisted, the more the engineering systems
                        around the model matter. Component contracts matter. Documentation matters.
                        Repository structure matters. Design tokens matter. Validation matters. And{" "}
                        <strong>context becomes part of the architecture</strong>.
                    </Para>
                    <Para>
                        The goal was not to replace engineers with AI. It was to engineer an
                        environment where <strong>humans and AI could build from the same
                        system</strong>.
                    </Para>
                </Section>
            </Body>
        </Layout>

        <NDADisclosure />
    </PageWrapper>
);

export default Att;
